import { numberOrderStops } from '../../helpers/assignmentOrderStops'
import { waypointArrival, waypointDeparture } from '../../helpers/waypointTimes'
import { WaypointCard, YardWaypointCard } from './WaypointCard'
import { WaypointNumber } from './WaypointNumber'
import type { TimelineDate, TimelineWaypoint } from './types'

const STATUS_CLASS: Record<string, string> = {
  completed: 'route-timeline__item--completed',
  inProgress: 'route-timeline__item--active',
}

/**
 * Vertical route timeline (v1 RouteOverview). Order stops open the page that
 * `buildOrderPath` builds; without it (cancelled assignment) they are plain cards.
 */
export const RouteTimeline = ({
  waypoints,
  datetime,
  buildOrderPath,
}: {
  waypoints: TimelineWaypoint[]
  datetime: TimelineDate
  buildOrderPath: ((orderId: number) => string) | null
}) => {
  if (waypoints.length === 0) return null

  // Order stops are numbered counting order waypoints only (shared with the
  // Lines on Site and Instructions blocks, so "Stop #1" means the same everywhere)
  const stopNumberByWaypointId = new Map(
    numberOrderStops(waypoints).map((stop) => [
      stop.waypoint.id,
      stop.stopNumber,
    ]),
  )

  return (
    <div className='route-timeline'>
      {waypoints.map((wp, index) => {
        const arrival = waypointArrival(wp)
        const departure = waypointDeparture(wp)
        const isFirst = index === 0
        const isLast = index === waypoints.length - 1

        const wpStatus = wp.status as string
        const isCompleted = wpStatus === 'completed'
        const wpStatusClass = STATUS_CLASS[wpStatus] ?? ''

        const stopNumber = stopNumberByWaypointId.get(wp.id)

        return (
          <div key={wp.id}>
            <div
              className={`route-timeline__item ${wpStatusClass} ${isFirst ? 'route-timeline__item--first' : ''} ${isLast ? 'route-timeline__item--last' : ''}`}
            >
              <WaypointNumber
                isFirst={isFirst}
                isLast={isLast}
                isCompleted={isCompleted}
                stopNumber={stopNumber}
              />
              {wp.order ? (
                <WaypointCard
                  waypoint={wp}
                  linkTo={buildOrderPath?.(wp.order.id)}
                  arrival={arrival}
                  departure={departure}
                  datetime={datetime}
                />
              ) : (
                <YardWaypointCard
                  waypoint={wp}
                  isFirst={isFirst}
                  isLast={isLast}
                  arrival={arrival}
                  departure={departure}
                  datetime={datetime}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
